/**
 * @file Box atom — generic surface/container primitive (RFC §3).
 * @description Replaces raw `View` in app code. className carries the closed
 * layout/surface vocabulary; UNSAFE_style is the counted escape hatch.
 * @module components/atoms/Box
 */
import React from "react";
import { Platform } from "react-native";

import { Box as UIBox } from "@/components/ui/box";

import type { WithUnsafeStyle } from "../types";
import type {
  AccessibilityProps,
  AccessibilityRole,
  LayoutChangeEvent,
} from "react-native";

/**
 *
 */
type UIBoxProps = React.ComponentProps<typeof UIBox>;

/**
 *
 */
export interface BoxProps
  extends Omit<UIBoxProps, "style">, WithUnsafeStyle<UIBoxProps["style"]> {
  /** Test/a11y identity is part of the atom API (RFC standing constraint 4). */
  testID?: string;
  /**
   * Non-interactive accessibility role for this container (e.g. `"alert"`,
   * `"image"`, `"status"`, landmark roles). On web it maps to the DOM `role`
   * attribute; on native it flows to the underlying view. Interactive roles
   * (`button`/`link`/`tab`) belong on the `Pressable` atom, not here.
   */
  accessibilityRole?: AccessibilityRole;
  /**
   * Accessible name announced by assistive tech. On web it maps to
   * `aria-label`; on native it flows to the underlying view.
   */
  accessibilityLabel?: string;
  /**
   * Live-region politeness for dynamic content. On web a value other than
   * `"none"` maps to `aria-live`; on native it flows to the underlying view.
   */
  accessibilityLiveRegion?: AccessibilityProps["accessibilityLiveRegion"];
  /**
   * Supplementary hint announced after the label. Flows to the underlying
   * view on native; web has no standard ARIA equivalent so it passes through
   * untouched.
   */
  accessibilityHint?: string;
  /**
   * Typed measurement callback fired with the laid-out frame
   * (`{ x, y, width, height }`). On native RN consumes it via `{...props}`; the
   * web Box renders a raw `div` whose prop surface doesn't expose `onLayout`, so
   * forwarding it here makes layout measurement work on both platforms. This is
   * a measurement callback, not a styling escape hatch.
   */
  onLayout?: (event: LayoutChangeEvent) => void;
}

const Box = React.forwardRef<React.ComponentRef<typeof UIBox>, BoxProps>(
  function Box({ UNSAFE_style, ...props }, ref) {
    const {
      testID,
      accessibilityRole,
      accessibilityLabel,
      accessibilityLiveRegion,
    } = props;
    // web Box renders a raw div, so RN accessibility props are not
    // auto-mapped; translate the non-interactive subset to ARIA here. On
    // native the props flow through {...props} unchanged.
    const webA11y =
      Platform.OS === "web"
        ? {
            ...(accessibilityRole ? { role: accessibilityRole } : {}),
            ...(accessibilityLabel ? { "aria-label": accessibilityLabel } : {}),
            ...(accessibilityLiveRegion && accessibilityLiveRegion !== "none"
              ? { "aria-live": accessibilityLiveRegion }
              : {}),
          }
        : {};
    return (
      <UIBox
        ref={ref}
        style={UNSAFE_style}
        // web Box renders a raw div where bare testID is not data-testid;
        // emit both so the e2e contract (standing constraint 4) holds.
        {...(testID ? ({ "data-testid": testID } as object) : {})}
        {...(webA11y as object)}
        {...props}
      />
    );
  }
);

Box.displayName = "Box";

export { Box };
